// barChart, clusteredBarChart — horizontal bars.

import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import {
  applySelectionStyle,
  baseChartOption,
  bucketKey,
  bucketKeys,
  firstFieldRef,
  isEmpty,
  makeCategoryClick,
  pbiAxisDefault, // AGENT-C: dotted gridlines + Segoe UI axis labels.
  pickColumn,
} from "./_echartsHelpers";

export function BarChartVisual({ spec, data, filters, onFilter }: VisualProps) {
  const empty = isEmpty(data);
  const rows = data.rows ?? [];
  const fields = spec.fields ?? {};
  const categoryKey = bucketKey(fields.Category);
  const valueKeys = bucketKeys(fields.Y).length
    ? bucketKeys(fields.Y)
    : bucketKeys(fields.Values);
  const seriesKeys = bucketKeys(fields.Series);
  const onClick = makeCategoryClick(
    { visualId: spec.id, spec, onFilter },
    firstFieldRef(fields.Category),
  );

  const categoryValues = categoryKey ? (pickColumn(rows, categoryKey) as string[]) : [];

  const baseOption: EChartsOption = {
    ...baseChartOption(),
    // AGENT-C: merge PBI axis defaults (dotted gridlines, Segoe UI labels) in.
    yAxis: {
      ...pbiAxisDefault,
      type: "category",
      data: categoryValues,
      inverse: true,
    },
    xAxis: { ...pbiAxisDefault, type: "value" },
    series: valueKeys.map((k) => ({
      type: "bar",
      name: k,
      data: pickColumn(rows, k) as number[],
      stack: seriesKeys.length > 0 ? "total" : undefined,
    })),
  };

  // P1-selection: dim non-selected categories when a matching filter is active.
  const categoryFieldRef = firstFieldRef(fields.Category);
  const categoryKeyFq = categoryFieldRef?.table && categoryFieldRef?.column
    ? `${categoryFieldRef.table}.${categoryFieldRef.column}`
    : categoryKey;
  const option = applySelectionStyle(baseOption, filters ?? {}, categoryKeyFq, categoryValues);

  return (
    <ChartShell title={spec.title} isEmpty={empty} status={data.status}>
      <ReactECharts
        option={option}
        notMerge
        style={{ height: "100%", width: "100%" }}
        onEvents={{ click: onClick }}
      />
    </ChartShell>
  );
}
